"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Check, Share2 } from "lucide-react";
import { jukeboxHref } from "@/lib/jukebox-links";

export function JukeboxShareButton({ playlistHref, eventId, artistId, artistName, eventName }: {
  playlistHref: string; eventId: number; artistId: number; artistName: string; eventName: string;
}) {
  const t = useTranslations("Jukebox");
  const [status, setStatus] = useState<"idle" | "copied" | "error">("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  async function share() {
    setStatus("idle");
    const url = new URL(jukeboxHref(playlistHref, eventId, artistId), window.location.origin).toString();
    if (navigator.share) {
      try {
        await navigator.share({ title: `${artistName} · ${eventName}`, url });
        return;
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setStatus("copied");
    } catch {
      setStatus("error");
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setStatus("idle"), 3000);
  }

  const label = status === "copied" ? t("linkCopied") : t("shareArtist", { artist: artistName });
  return (
    <div className="absolute bottom-4 right-3 z-10">
      <button type="button" onClick={share} aria-label={label} title={label}
        className="flex h-11 w-11 items-center justify-center rounded-full bg-black/70 text-white transition-colors hover:bg-black/90 hover:text-acid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acid">
        {status === "copied" ? <Check className="h-5 w-5" aria-hidden="true" /> : <Share2 className="h-5 w-5" aria-hidden="true" />}
      </button>
      <span role="status" className={status === "idle" ? "sr-only" : "absolute bottom-full right-0 mb-2 w-max max-w-[250px] rounded bg-black px-3 py-2 text-xs text-white"}>
        {status === "copied" ? t("linkCopied") : status === "error" ? t("shareError") : ""}
      </span>
    </div>
  );
}
